import { useState, type FocusEvent } from "react";
import { useTranslation } from "react-i18next";
import { Controller, type Control, type FieldErrors, type UseFormSetError, type UseFormClearErrors } from "react-hook-form";
import Select from "react-select";
import type { NewDeviceFormType } from "../types";
import { getCustomSelectStyles } from "../../../../shared";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { useCompaniesStore } from "../../../../../../store/admin/companiesStore";
import { checkVehiclePlateAvailable } from "../../../../../../features/devices/mutations";
import { useSchemaOverrides } from "../../../../../../hooks/useSchemaOverrides";
import {
  getVehicleTypeOptions,
  getFuelTypeOptions,
} from "../utils/inlineEntityOptions";

interface Props {
  control: Control<NewDeviceFormType>;
  errors: FieldErrors<NewDeviceFormType>;
  register: any;
  isDarkMode: boolean;
  getInputClassName: (hasError: boolean) => string;
  setError: UseFormSetError<NewDeviceFormType>;
  clearErrors: UseFormClearErrors<NewDeviceFormType>;
}

export function VehicleInlineForm({
  control,
  errors,
  register,
  isDarkMode,
  getInputClassName,
  setError,
  clearErrors,
}: Props) {
  const { t } = useTranslation("adminSections");
  const { isRequired } = useSchemaOverrides("vehicles", "postVehicle");
  const { statuses } = useStatusesStore();
  const selectedCompanyId = useCompaniesStore((s) => s.companyId);
  const statusCompanyId = selectedCompanyId || localStorage.getItem("userCompanyId");
  const [checkingPlate, setCheckingPlate] = useState(false);

  const vehicleTypeOptions = getVehicleTypeOptions(t);
  const fuelTypeOptions = getFuelTypeOptions(t);
  const unitStatusOptions = getStatusOptionsForEntity(statuses, "units", t, statusCompanyId);
  const errorClassName = "text-red-500 text-xs mt-1";
  const labelClass = `text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"} mb-1`;

  const handlePlateBlur = async (e: FocusEvent<HTMLInputElement>) => {
    const trimmed = e.target.value.trim();
    if (!trimmed) { clearErrors("vehicle_plate"); return; }
    setCheckingPlate(true);
    try {
      const result = await checkVehiclePlateAvailable(trimmed);
      if (!result.available) {
        setError("vehicle_plate", { type: "manual", message: result.message! });
      } else {
        clearErrors("vehicle_plate");
      }
    } finally {
      setCheckingPlate(false);
    }
  };

  return (
    <>
      <div className="flex flex-col">
        <label className={labelClass}>
          {t("units.fields.name", { defaultValue: "Nombre" })} {isRequired("name") && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          placeholder={t("units.placeholders.name", { defaultValue: "Ej. Camión Volvo 2024" })}
          className={getInputClassName(!!errors.vehicle_name)}
          {...register("vehicle_name", {
            required: isRequired("name") ? t("units.form.requiredField") : false,
            minLength: { value: 3, message: t("units.form.minLength3") },
          })}
        />
        {errors.vehicle_name && <span className={errorClassName}>{errors.vehicle_name.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("units.fields.plate", { defaultValue: "Placa" })} {isRequired("plate") && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          placeholder={t("units.placeholders.plate", { defaultValue: "Ej. ABC-1234" })}
          className={getInputClassName(!!errors.vehicle_plate)}
          {...register("vehicle_plate", {
            required: isRequired("plate") ? t("units.form.requiredField") : false,
            minLength: { value: 6, message: t("units.form.minLength6") },
            maxLength: { value: 8, message: t("units.form.maxLength") },
          })}
          onBlur={handlePlateBlur}
        />
        {checkingPlate && (
          <span className="text-xs text-gray-400">{t("devices.validation.checkingPlate", { defaultValue: "Verificando placa..." })}</span>
        )}
        {errors.vehicle_plate && <span className={errorClassName}>{errors.vehicle_plate.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("units.fields.type", { defaultValue: "Tipo" })} {isRequired("type") && <span className="text-red-500">*</span>}
        </label>
        <Controller
          name="vehicle_type"
          control={control}
          rules={{ required: isRequired("type") ? t("units.form.requiredField") : false }}
          render={({ field }) => (
            <Select
              options={vehicleTypeOptions}
              styles={getCustomSelectStyles(isDarkMode, !!errors.vehicle_type)}
              placeholder={t("units.placeholders.selectType", { defaultValue: "Selecciona tipo" })}
              value={vehicleTypeOptions.find((o) => o.value === field.value) || null}
              onChange={(opt: any) => field.onChange(opt?.value || "car")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
        {errors.vehicle_type && <span className={errorClassName}>{errors.vehicle_type.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("units.fields.brand", { defaultValue: "Marca" })} {isRequired("brand") && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          placeholder={t("units.placeholders.brand", { defaultValue: "Ej. Kenworth, Volvo" })}
          className={getInputClassName(!!errors.vehicle_brand)}
          {...register("vehicle_brand", {
            required: isRequired("brand") ? t("units.form.requiredField") : false,
          })}
        />
        {errors.vehicle_brand && <span className={errorClassName}>{errors.vehicle_brand.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("units.fields.model", { defaultValue: "Modelo" })} {isRequired("model") && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          placeholder={t("units.placeholders.model", { defaultValue: "Ej. T680, FH16" })}
          className={getInputClassName(!!errors.vehicle_model)}
          {...register("vehicle_model", {
            required: isRequired("model") ? t("units.form.requiredField") : false,
          })}
        />
        {errors.vehicle_model && <span className={errorClassName}>{errors.vehicle_model.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("units.fields.year", { defaultValue: "Año" })} {isRequired("year") && <span className="text-red-500">*</span>}
        </label>
        <input
          type="number"
          min={1900}
          max={new Date().getFullYear()}
          placeholder={t("units.placeholders.year", { defaultValue: "Ej. 2024" })}
          className={getInputClassName(!!errors.vehicle_year)}
          {...register("vehicle_year", {
            required: isRequired("year") ? t("units.form.requiredField") : false,
            min: { value: 1900, message: t("units.form.yearMin") },
            max: { value: new Date().getFullYear(), message: t("units.form.yearMax") },
          })}
        />
        {errors.vehicle_year && <span className={errorClassName}>{errors.vehicle_year.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("units.fields.fuelType", { defaultValue: "Tipo de combustible" })} {isRequired("fuelType") && <span className="text-red-500">*</span>}
        </label>
        <Controller
          name="vehicle_fuelType"
          control={control}
          rules={{ required: isRequired("fuelType") ? t("units.form.requiredField") : false }}
          render={({ field }) => (
            <Select
              options={fuelTypeOptions}
              styles={getCustomSelectStyles(isDarkMode, !!errors.vehicle_fuelType)}
              placeholder={t("units.placeholders.selectFuelType", { defaultValue: "Selecciona combustible" })}
              value={fuelTypeOptions.find((o) => o.value === field.value) || null}
              onChange={(opt: any) => field.onChange(opt?.value || "")}
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
        {errors.vehicle_fuelType && <span className={errorClassName}>{errors.vehicle_fuelType.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("units.fields.status", { defaultValue: "Estado" })} {isRequired("status_id") && <span className="text-red-500">*</span>}
        </label>
        <Controller
          name="vehicle_status_id"
          control={control}
          rules={{ required: isRequired("status_id") ? t("units.form.requiredField") : false }}
          render={({ field }) => (
            <Select
              options={unitStatusOptions}
              styles={getCustomSelectStyles(isDarkMode, !!errors.vehicle_status_id)}
              placeholder={t("units.placeholders.selectStatus", { defaultValue: "Selecciona estado" })}
              value={unitStatusOptions.find((o) => o.value === field.value) || null}
              onChange={(opt: any) => field.onChange(opt?.value || "")}
              isClearable
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
        {errors.vehicle_status_id && <span className={errorClassName}>{errors.vehicle_status_id.message}</span>}
      </div>
    </>
  );
}
